import React from 'react'
import { NextPage } from 'next'
import { PagePropsWrapper, SEOFriendlyInitialProps } from 'lib/routing/nextPageUtils'
import {
	FeaturedConstraint,
	NotHiddenConstraint,
	TopicConstraint,
	TopicProductConstraint
} from 'services/ContentfulService/constraints'
import {
	Article,
	ArticleSummary,
	enhanceArticle,
	summarizeArticle,
	summarizeArticles
} from 'services/ContentfulService/models/articles'
import { getTopicBySlug, getTopics } from 'services/ContentfulService/hooks/topics'
import { Topic, TopicTypeSorter } from 'services/ContentfulService/models/topics'
import { fetchArticles, UseArticlesInterface } from 'services/ContentfulService/hooks/articles'
import { ContentfulHookProps } from 'services/ContentfulService/hooks/template'
import { NamedPromises, truncArray } from 'lib'
import { FAQ } from 'services/ContentfulService/models/fact'
import { TopicDetailPage } from 'experiences/learn/topics/TopicDetailPage'
import { getFaqsByTopic } from 'services/ContentfulService/hooks/faqs'
import { StandardLayout } from 'shared/layout/StandardLayout'
import { referencesMultiple } from 'services/ContentfulService/utils'
import { ObjectNotFoundError } from 'services/ContentfulService/errors'

interface Props {
	topic: Topic
	articles: ArticleSummary[]
	featuredArticle?: Article
	relatedTopics: Topic[]
	faqs: FAQ[]
}

const LearnTopicPage: NextPage<PagePropsWrapper<Props>> = ({ metaTags, pageProps }) => {
	const summary = pageProps.featuredArticle && summarizeArticle(pageProps.featuredArticle)
	return (
		<StandardLayout metaTags={metaTags}>
			<TopicDetailPage
				relatedTopics={truncArray(pageProps.relatedTopics, 4)}
				topArticles={pageProps.articles}
				featuredArticle={summary}
				relatedProducts={[]}
				topic={pageProps.topic}
				faqs={pageProps.faqs}
			/>
		</StandardLayout>
	)
}

//TODO: Move to server side props
LearnTopicPage.getInitialProps = SEOFriendlyInitialProps<Props>(
	async function (ctx) {
		const { query } = ctx
		if (typeof query.topic === 'string') {
			const topic = await getTopicBySlug(query.topic)
			const articlesInput: ContentfulHookProps = { constraints: [TopicConstraint(topic.id)], count: 4 }
			const featuredArticleInput: UseArticlesInterface = {
				constraints: [FeaturedConstraint(), TopicConstraint(topic.id)],
				count: 1
			}
			const topicsPromise = referencesMultiple<Topic>(
				(topic.products || []).map(p => p.id),
				id => getTopics({ constraints: [TopicProductConstraint(id), NotHiddenConstraint()], count: 5 })
			)
			const response = await NamedPromises({
				topic: Promise.resolve(topic),
				featuredArticle: fetchArticles(featuredArticleInput)
					.then(articles => articles.shift())
					.then(article => (article ? enhanceArticle(article) : undefined)),
				relatedTopics: topicsPromise.then(topics => topics.filter(t => t.id != topic.id).sort(TopicTypeSorter)),
				articles: fetchArticles(articlesInput).then(summarizeArticles),
				faqs: getFaqsByTopic(topic)
			})
			if (response.featuredArticle) {
				response.articles = response.articles.filter((a: ArticleSummary) => a.id != response.featuredArticle.id)
			} else {
				response.featuredArticle = response.articles.shift()
			}

			return response
		}
		throw new ObjectNotFoundError('topic')
	},
	(metadataTags, pageProps) => {
		return {
			...metadataTags,
			description: pageProps.topic.subtitle,
			imageUrl: pageProps.topic.graphic?.url,
			title: `Learn About ${pageProps.topic.name} | Fiona`
		}
	}
)

export default LearnTopicPage
